<template>
    <v-container class="pa-5" fluid>
        <AssignComponentDialog
            :is-open="assignComponentDialog"
            :product-components="productComponents"
            @on-save="addProductComponent"
            @on-close="closeAssignComponentDialog"
        />

        <AssignProductionPropertiesDialog
            :is-open="assignProductionPropertiesDialog"
            :component="currentProductionComponent"
            :properties="productProductionProperties"
            @on-save="saveProductProductionProperties"
            @on-close="closeAssignProductionPropertiesDialog"
        />

        <AssignProductionProcessDialog
            :is-open="assignProductionProcessDialog"
            :component="currentProductionComponent"
            :processes="productionProcesses"
            @on-save="saveProductionProcess"
            @on-close="closeProductionProcessDialog"
        />

        <ConfirmDialog
            :is-open="confirmRemoveComponentDialog"
            :data="currentProductionComponentIndex"
            @on-confirm="removeProductComponent(currentProductionComponentIndex)"
            @on-abort="closeConfirmRemoveComponentDialog"
        >
            <template #title>
                Usunąć komponent z produktu?
            </template>
            <template #content>
                Czy na pewno chcesz usunąć komponent?
            </template>
        </ConfirmDialog>

        <ConfirmDialog
            :is-open="confirmLeaveDialog"
            @on-confirm="acceptConfirmLeaveDialog"
            @on-abort="closeConfirmLeaveDialog"
        >
            <template #title>
                Opuścić stronę?
            </template>
            <template #content>
                Szablon produktu nie został zapisany. Czy chcesz opuścić stronę?
            </template>
        </ConfirmDialog>

        <AssignProductTemplateDialog
            :is-open="assignProductTemplateDialog"
            @on-save="addProductTemplate"
            @on-close="closeAssignProductTemplateDialog"
        />

        <page-title>{{ pageTitle }}</page-title>

        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Parametry produktu
                            </h2>
                        </v-col>
                        <v-col v-if="actionName === 'addProduct'" cols="auto">
                            <v-btn data-sentry-label="Wybierz z szablonu" color="primary" text @click="showAssignProductTemplateDialog">
                                Wybierz z szablonu
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-container>
                                    <v-row align="center">
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                v-model="productTemplate.name"
                                                label="Nazwa produktu"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productTemplateNameErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-autocomplete
                                                v-model="productTemplate.jsonTemplate.productTypeId"
                                                :items="products"
                                                label="Rodzaj produktu"
                                                item-text="name"
                                                item-value="id"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productTemplateProductTypeErrors()"
                                                @change="handleProductTypeChange()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="productTemplate.jsonTemplate.formatA"
                                                label="Szerokość"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :error-messages="productTemplateFormatAErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="productTemplate.jsonTemplate.formatB"
                                                label="Wysokość"
                                                suffix="mm"
                                                outlined
                                                dense
                                                :error-messages="productTemplateFormatBErrors()"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="product.amount"
                                                v-mask="'#######'"
                                                label="Nakład"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productAmountErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="2">
                                            <v-text-field
                                                v-model="product.numberOfVersions"
                                                v-mask="'#######'"
                                                label="Ilość wzorów"
                                                :disabled="!isAllowProofOrder"
                                                outlined
                                                dense
                                                :error-messages="productNumberOfVersionsErrors()"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-autocomplete
                                                v-if="isAllowProofOrder"
                                                v-model="product.destinyPaperId"
                                                :items="papers"
                                                label="Rodzaj papieru docelowej produkcji"
                                                item-text="name"
                                                item-value="id"
                                                required
                                                outlined
                                                dense
                                                :error-messages="productDestinyPaperErrors()"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-textarea
                                                v-model="productTemplate.jsonTemplate.description"
                                                label="Uwagi"
                                                outlined
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="actionName === 'addProduct'" cols="12" md="4">
                                            <v-checkbox
                                                v-model="saveProductTemplate"
                                                label="Zapisz jako nowy szablon"
                                                class="mt-0"
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Pliki produkcyjne
                            </h2>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-card :loading="actionLoading.fileRemoving" loader-height="3">
                                <v-overlay
                                    color="white"
                                    opacity="0.85"
                                    absolute
                                    :value="actionLoading.fileUploading"
                                >
                                    <v-container class="pa-0">
                                        <v-row justify="center">
                                            <v-col cols="auto">
                                                <v-progress-circular
                                                    :rotate="-90"
                                                    :size="120"
                                                    :width="4"
                                                    :value="uploadingProgressValue"
                                                    color="primary"
                                                >
                                                    {{ uploadingProgressValue }}
                                                </v-progress-circular>
                                            </v-col>
                                        </v-row>
                                        <v-row justify="center">
                                            <v-col cols="auto">
                                                <v-btn
                                                    color="error"
                                                    small
                                                    data-sentry-label="Anuluj"
                                                    outlined
                                                    @click="abortUploadingProductionFileAttachment"
                                                >
                                                    Anuluj
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-overlay>
                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-col cols="auto">
                                            <v-radio-group v-model="selectedFileInput" class="mt-n4" @change="onRadioChange">
                                                <v-row>
                                                    <v-col cols="auto" sm="8" md="6" lg="8">
                                                        <v-radio label="Wgraj plik do systemu" value="upload" class="font-weight-bold" />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12" sm="8" md="6" lg="8">
                                                        <v-file-input
                                                            v-model="currentProductionFile"
                                                            label="Plik produkcyjny"
                                                            :disabled="selectedFileInput !== 'upload'"
                                                            counter
                                                            placeholder="Wybierz plik"
                                                            hint="Wymagany plik PDF. Dozwolone znaki: A-Z, 0-9, - oraz _"
                                                            persistent-hint
                                                            prepend-icon="mdi-paperclip"
                                                            outlined
                                                            dense
                                                            :show-size="1000"
                                                            :rules="currentProductionFileRules"
                                                            :error-messages="currentProductionFileErrors()"
                                                        >
                                                            <template #selection="{index, text}">
                                                                <v-chip
                                                                    v-if="index < 2"
                                                                    label
                                                                    small
                                                                >
                                                                    {{ text }}
                                                                </v-chip>
                                                            </template>
                                                        </v-file-input>
                                                    </v-col>
                                                    <v-col cols="auto">
                                                        <v-btn
                                                            color="primary"
                                                            outlined
                                                            data-sentry-label="Wyślij plik"
                                                            :disabled="!canUploadFile()"
                                                            @click="addProductionFileAttachment"
                                                        >
                                                            <v-icon left>
                                                                mdi-file-upload
                                                            </v-icon>
                                                            Wyślij
                                                        </v-btn>
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="auto" sm="8" md="6" lg="8">
                                                        <v-radio label="Podaj link do pliku produkcyjnego" value="link" class="font-weight-bold" />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="auto" sm="8" md="6" lg="8">
                                                        <v-text-field
                                                            v-model="fileLink"
                                                            label="Link do pliku"
                                                            :disabled="selectedFileInput !== 'link'"
                                                            placeholder="Wklej link do plików"
                                                            outlined
                                                            dense
                                                            prepend-icon="mdi-link"
                                                        />    
                                                    </v-col>
                                                    <v-col cols="auto">
                                                        <v-btn
                                                            color="primary"
                                                            outlined
                                                            data-sentry-label="Załącz link"
                                                            :disabled="!fileLink"
                                                            @click="addProductionFileAttachmentLink"
                                                        >
                                                            <v-icon left>
                                                                mdi-file-upload
                                                            </v-icon>
                                                            Wyślij
                                                        </v-btn>
                                                    </v-col>
                                                </v-row>
                                            </v-radio-group>
                                        </v-col>
                                    </v-container>

                                    <v-data-table
                                        v-if="product.productionFiles.length > 0"
                                        :items="product.productionFiles"
                                        :headers="headers"
                                        disable-pagination
                                        hide-default-footer
                                        :mobile-breakpoint="0"
                                    >
                                        <template #item.fileName="{item}">
                                            {{ productionFileName(item) }}
                                        </template>
                                        <template #item.actions="{item}">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń plik"
                                                            v-on="on"
                                                            @click="deleteProductionFileAttachment(item.id)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                    </v-data-table>

                                    <v-alert
                                        v-else
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Nie dodano pliku produkcyjnego.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                color="error"
                                medium
                                data-sentry-label="Wstecz"
                                class="mr-3"
                                :disabled="actionLoading.fileUploading || actionLoading.fileRemoving"
                                @click="close"
                            >
                                Wstecz
                            </v-btn>
                            <v-btn
                                color="success"
                                medium
                                data-sentry-label="Zapisz"
                                :disabled="actionLoading.fileUploading || actionLoading.fileRemoving"
                                @click="saveProduct"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Komponenty
                            </h2>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="secondary"
                                fab
                                small
                                data-sentry-label="Dodaj komponent"
                                class="elevation-1"
                                @click="showAssignComponentDialog"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col v-if="productTemplate.jsonTemplate.orderProductComponents.length === 0" cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Produkt nie posiada przypisanych komponentów.
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <template v-else>
                            <v-col v-for="(component, componentIndex) in productTemplate.jsonTemplate.orderProductComponents" :key="component.id" cols="12">
                                <v-card>
                                    <v-card-title>
                                        <v-row align="center">
                                            <v-col>
                                                <div class="text-h6 text-no-wrap">
                                                    {{ component.component.name }} ({{ component.component.code }})
                                                </div>
                                            </v-col>
                                            <v-col sm="auto">
                                                <v-btn
                                                    color="primary"
                                                    medium
                                                    data-sentry-label="Dodaj cechy produkcyjne"
                                                    text
                                                    class="mx-1"
                                                    @click="showAssignProductionPropertiesDialog(component, componentIndex)"
                                                >
                                                    Dodaj cechy produkcyjne
                                                </v-btn>
                                                <v-btn
                                                    color="primary"
                                                    medium
                                                    data-sentry-label="Dodaj procesy produkcyjne"
                                                    text
                                                    class="mx-1"
                                                    @click="showAssignProductionProcessDialog(component, componentIndex)"
                                                >
                                                    Dodaj procesy produkcyjne
                                                </v-btn>
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Kopiuj komponent"
                                                            v-on="on"
                                                            @click="copyComponent(component)"
                                                        >
                                                            <v-icon color="blue">
                                                                mdi-content-copy
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Kopiuj</span>
                                                </v-tooltip>
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń komponent"
                                                            v-on="on"
                                                            @click="showConfirmRemoveComponentDialog(componentIndex)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń komponent</span>
                                                </v-tooltip>
                                            </v-col>
                                        </v-row>
                                    </v-card-title>

                                    <v-card-text>
                                        <v-row v-if="component.component.code === 'skladka'">
                                            <v-col cols="12" sm="8" lg="8">
                                                <div class="d-flex align-center">
                                                    <v-text-field
                                                        v-model="component.numberOfCopies"
                                                        v-mask="'###'"
                                                        label="Liczba składek"
                                                        min="1"
                                                        placeholder="Podaj liczbę składek"
                                                        outlined
                                                        dense
                                                        required
                                                        class="flex-grow-0"
                                                        :error-messages="numberOfCopiesErrors(component)"
                                                        @change="handleNumberOfCopiesInput()"
                                                        @focus="onNumberOfCopiesFocus(component)"
                                                    >
                                                        <template #append-outer>
                                                            <span 
                                                                class="ml-3 font-weight-bold black--text text-caption text-no-wrap"
                                                            >
                                                                {{ calculatePageRange(component) }}
                                                            </span>
                                                        </template>
                                                    </v-text-field>
                                                </div>
                                            </v-col>
                                        </v-row>
                                        <v-alert
                                            v-if="component.orderComponentProperties.length === 0"
                                            type="info"
                                            text
                                            border="left"
                                        >
                                            Brak przypisanych cech produkcyjnych.
                                        </v-alert>
                                        <v-row v-else>
                                            <v-col cols="12">
                                                <strong>Cechy produkcyjne:</strong>
                                            </v-col>
                                            <v-col
                                                v-for="(productionProperty, propertyIndex) in component.orderComponentProperties"
                                                :key="productionProperty.productProductionProperty.id"
                                                cols="12"
                                                sm="4"
                                                lg="3"
                                            >
                                                <div class="d-inline-flex">
                                                    <v-autocomplete
                                                        v-if="productionProperty.productProductionProperty.name === 'rodzaj_papieru'"
                                                        v-model="productionProperty.value"
                                                        :items="papers"
                                                        :filter="customFilter"
                                                        :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                        placeholder="wartość"
                                                        item-value="name"
                                                        item-text="name"
                                                        no-data-text="Nie znaleziono"
                                                        :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                        outlined
                                                        dense
                                                        required
                                                    />
                                                    <v-autocomplete
                                                        v-else-if="productionProperty.productProductionProperty.name === 'rodzaj_skladania'"
                                                        v-model="productionProperty.value"
                                                        :items="foldingTypes"
                                                        :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                        placeholder="wartość"
                                                        item-value="name"
                                                        item-text="name"
                                                        no-data-text="Nie znaleziono"
                                                        :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                        outlined
                                                        dense
                                                        required
                                                        @change="handleFoldingTypeChange(productionProperty)"
                                                    />
                                                    <v-text-field
                                                        v-else-if="productionProperty.productProductionProperty.name === 'ilosc_falcow'"
                                                        v-model="productionProperty.value"
                                                        v-mask="'##'"
                                                        :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                        placeholder="wartość"
                                                        type="text"
                                                        :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                        outlined
                                                        dense
                                                        required
                                                        @input="enforceMinValue(componentIndex, propertyIndex, 1)"
                                                    />
                                                    <v-text-field
                                                        v-else
                                                        v-model="productionProperty.value"
                                                        :label="productionProperty.productProductionProperty.verboseName + ':'"
                                                        placeholder="wartość"
                                                        :error-messages="productionPropertyValueErrors(componentIndex, propertyIndex)"
                                                        outlined
                                                        dense
                                                        required
                                                    />
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Usuń cechę produkcyjną"
                                                        class="mt-1"
                                                        @click="removeProductProductionProperty(componentIndex, productionProperty)"
                                                    >
                                                        <v-icon>
                                                            mdi-close
                                                        </v-icon>
                                                    </v-btn>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-divider />
                                    <v-card-text>
                                        <v-alert
                                            v-if="component.orderComponentProcesses.length === 0"
                                            type="info"
                                            text
                                            border="left"
                                        >
                                            Brak przypisanych procesów produkcyjnych.
                                        </v-alert>
                                        <v-row v-else>
                                            <v-col cols="12">
                                                <strong>Procesy produkcyjne:</strong>
                                            </v-col>
                                            <v-col>
                                                <v-chip
                                                    v-for="productionProcess in component.orderComponentProcesses"
                                                    :key="productionProcess.productionProcess.id"
                                                    class="ma-2"
                                                    close
                                                    @click:close="removeProductionProcess(componentIndex, productionProcess)"
                                                >
                                                    {{ productionProcess.productionProcess.name }}
                                                </v-chip>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </template>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import AssignComponentDialog from '@/views/Configurations/Common/AssignComponentDialog'
import AssignProductionPropertiesDialog from '@/views/Configurations/Common/AssignProductionPropertiesDialog'
import AssignProductionProcessDialog from '@/views/Configurations/Common/AssignProductionProcessDialog'
import AssignProductTemplateDialog from './AssignProductTemplateDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import tooltipDelay from '@/mixins/tooltip'
import customFilters from '@/mixins/customFilters'
import { errorHandler } from '@/utils/errorHandler'
import { appConfig } from '@/config'
import { required, integer, minValue, requiredIf } from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'
import { pdfFileName } from '@/utils/customValidators'

const maxFileSize = (value) => {
    return value.size < appConfig.orders.productionFileAttachmentSize * 1024 * 1024
}

const maxFileSizeProof = (value) => {
    return value.size < appConfig.orders.productionFileAttachmentSizeProof * 1024 * 1024
}

export default {
    name: 'Product',
    components: {
        PageTitle,
        AssignComponentDialog,
        AssignProductionPropertiesDialog,
        AssignProductionProcessDialog,
        ConfirmDialog,
        AssignProductTemplateDialog
    },
    directives: { mask },
    mixins: [
        tooltipDelay,
        customFilters
    ],
    beforeRouteEnter(to, from, next) {
        if (from.name) {
            Promise.all([
                store.dispatch('configurations/getProducts', {
                    shouldSavedInStore: false
                }),
                store.dispatch('configurations/getProductProductionProperties', {
                    query: '?orderAvailable=true'
                }),
                store.dispatch('configurations/getProductComponents'),
                store.dispatch('configurations/getProductionProcesses', {paginate: '', query: '?isVirtual=false'}),
                store.dispatch('configurations/getPapers'),
                store.dispatch('configurations/getFoldingTypes')
            ])
                .then(responses => {
                    next(vm => {
                        vm.products = responses[0].data.results
                        vm.productProductionProperties = responses[1].data.results
                        vm.productComponents = responses[2].data.results
                        vm.productionProcesses = responses[3].data.results
                        vm.papers = responses[4].data.results
                        vm.foldingTypes = responses[5].data.results
                        vm.productTemplateIndex = to.params.index
                        vm.pageTitle = to.meta.title
                        if (typeof to.params.index !== 'undefined') { // Product edit
                            vm.actionName = 'updateProduct'
                            vm.product.amount = store.state.orders.orderCreate.order.orderProducts[to.params.index].amount
                            vm.product.numberOfVersions = store.state.orders.orderCreate.order.orderProducts[to.params.index].numberOfVersions
                            vm.product.weight = store.state.orders.orderCreate.order.orderProducts[to.params.index].weight
                            vm.product.productionFiles = store.state.orders.orderCreate.order.orderProducts[to.params.index].productionFiles
                            vm.product.destinyPaperId = store.state.orders.orderCreate.order.orderProducts[to.params.index].destinyPaperId
                            vm.productTemplate = Object.assign({}, store.state.orders.orderCreate.productTemplates[to.params.index])
                        } else {
                            vm.actionName = 'addProduct'
                        }
                    })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        } else {
            next({ name: 'ordersInternalOrdersOrderCreate' })
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasProductChanges) {
            this.confirmLeaveDialog = true
            this.confirmLeaveDialogNextRouteName = to.name
        } else {
            next()
        }
    },
    data() {
        return {
            papers: [],
            foldingTypes: [],
            actionName: null,
            pageTitle: '',
            product: {
                amount: null,
                numberOfVersions: 1, // Temporary hardcoded
                weight: 0, // Temporary hardcoded
                productionFiles: [],
                destinyPaperId: null
            },
            productTemplate: {
                name: null,
                jsonTemplate: {
                    productTypeId: null,
                    formatA: null,
                    formatB: null,
                    orderProductComponents: [],
                    description: null
                }
            },
            currentProductionFile: null,
            currentProductionComponent: {
                component: {
                    name: null,
                    code: null
                },
                orderComponentProperties: [],
                orderComponentProcesses: []
            },
            products: [],
            productComponents: [],
            productProductionProperties: [],
            productionProcesses: [],
            productTemplateIndex: null,
            currentProductionComponentIndex: null,
            assignComponentDialog: false,
            confirmRemoveComponentDialog: false,
            assignProductionPropertiesDialog: false,
            hasProductChanges: false,
            userEditedComponents: false,
            confirmLeaveDialog: false,
            confirmLeaveDialogNextRouteName: null,
            assignProductionProcessDialog: false,
            assignProductTemplateDialog: false,
            saveProductTemplate: false,
            actionLoading: {
                fileUploading: false,
                fileRemoving: false
            },
            fileLink: null,
            selectedFileInput: 'upload',
            headers: [
                {
                    text: 'Nazwa pliku / link',
                    value: 'fileName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ],
            currentProductionFileRules: [
                value => !value || (maxFileSize(value) || `Przekroczono dopuszczalny rozmiar pliku: ${appConfig.orders.productionFileAttachmentSize}MB.`),
                value => !value || (maxFileSizeProof(value) || !this.proofProductType || 'Plik jest zbyt duży, skorzystaj z opcji przesłania pliku poprzez link.'),
                value => !value || (pdfFileName(value?.name) ||
                    'Nazwa pliku zawiera niedozwolne znaki - /:\'"?*<>|()[]%$#@{}~^& lub format pliku jest niepoprawny (Wymagany plik PDF).'),
            ],
        }
    },
    validations() {
        const rules = {
            product: {
                amount: {
                    required
                },
                numberOfVersions: {
                    required,
                    minValue: minValue(1)
                },
            },
            productTemplate: {
                name: {
                    required
                },
                jsonTemplate: {
                    productTypeId: {
                        required
                    },
                    orderProductComponents: {
                        $each: {
                            orderComponentProperties: {
                                $each: {
                                    value: {
                                        required
                                    }
                                }
                            },
                            orderComponentProcesses: {
                                required
                            },
                            numberOfCopies: {
                                required: requiredIf(function(component) {
                                    return component.component && component.component.code === 'skladka'
                                }),
                                integer,
                                minValue: minValue(1)
                            }
                        }
                    }
                }
            }
        }

        if (this.currentProductionFile) {
            rules['currentProductionFile'] = {
                maxFileSize,
                isNameValid:  value => pdfFileName(value?.name)
            }
        }

        if (this.productTemplate.jsonTemplate.formatA) {
            rules.productTemplate.jsonTemplate['formatA'] = {
                integer
            }
        }

        if (this.productTemplate.jsonTemplate.formatB) {
            rules.productTemplate.jsonTemplate['formatB'] = {
                integer
            }
        }

        if (this.isAllowProofOrder) {
            rules.product.destinyPaperId = {
                required
            }
        }

        return rules
    },
    computed: {
        proofProductType() {
            return this.productTemplate.jsonTemplate.productTypeId === 43
        },
        uploadingProgressValue() {
            return this.$store.state.common.progress.value
        },
        isAllowProofOrder() {
            const isAlloeProofMap = this.products
                .filter(item => item.id === this.productTemplate.jsonTemplate.productTypeId)
                .map(item => {
                    return item.allowProofOrder
                })

            return isAlloeProofMap.length > 0 ? isAlloeProofMap[0] : false
        },
    },
    mounted() {
        this.assignSequentialIds()
    },
    methods: {
        enforceMinValue(componentIndex, propertyIndex, minValue) {
            const property = this.productTemplate.jsonTemplate.orderProductComponents[componentIndex].orderComponentProperties[propertyIndex]
            const value = parseInt(property.value)
            
            if (!isNaN(value) && value < minValue) {
                property.value = minValue.toString()
            }
        },
        isFoldingNumberRequired(foldingTypeName) {
            if (!foldingTypeName) return false
            
            const foldingType = this.foldingTypes.find(type => type.name === foldingTypeName)
            
            return foldingType && foldingType.numberOfFoldingsRequired
        },
        handleFoldingTypeChange(productionProperty) {
            const componentIndex = this.productTemplate.jsonTemplate.orderProductComponents.findIndex(
                comp => comp.orderComponentProperties.includes(productionProperty)
            )
            
            if (componentIndex !== -1) {
                const component = this.productTemplate.jsonTemplate.orderProductComponents[componentIndex]
                const foldingType = this.foldingTypes.find(type => type.name === productionProperty.value)
                
                // Check if the folding type requires specifying number of folds
                if (foldingType && foldingType.numberOfFoldingsRequired) {
                    const foldingNumberProperty = component.orderComponentProperties?.find(
                        prop => prop.productProductionProperty.name === 'ilosc_falcow'
                    )
                    
                    if (!foldingNumberProperty) {
                        const foldingNumberPropertyDef = this.productProductionProperties.find(
                            prop => prop.name === 'ilosc_falcow'
                        )
                        
                        if (foldingNumberPropertyDef) {
                            component.orderComponentProperties.push({
                                productProductionPropertyId: foldingNumberPropertyDef.id,
                                productProductionProperty: foldingNumberPropertyDef,
                                value: ''
                            })
                        }
                    }
                } else {
                    const foldingNumberPropertyIndex = component.orderComponentProperties.findIndex(
                        property => property.productProductionProperty.name === 'ilosc_falcow'
                    )
                    
                    if (foldingNumberPropertyIndex !== -1) {
                        component.orderComponentProperties.splice(foldingNumberPropertyIndex, 1)
                    }
                }
            }
            this.hasProductChanges = true
        },
        onNumberOfCopiesFocus(component) {
            if (!Number(component.numberOfCopies)) {
                component.numberOfCopies = ''
            }
        },
        copyComponent(item) {
            this.userEditedComponents = true
            this.hasProductChanges = true
            
            const componentCopy = JSON.parse(JSON.stringify(item))
            
            if (componentCopy.component.code === 'skladka') {
                componentCopy.numberOfCopies = 0
            }
            
            this.productTemplate.jsonTemplate.orderProductComponents.push(componentCopy)
            
            if (componentCopy.component.code === 'skladka') {
                this.assignSequentialIds()
                this.calculatePageRange(componentCopy)
            }
        },
        handleNumberOfCopiesInput() {
            this.hasProductChanges = true
            this.assignSequentialIds()
        },
        assignSequentialIds() {
            const signatureComponents = this.productTemplate.jsonTemplate.orderProductComponents
                .filter(comp => comp.component.code === 'skladka')
            
            signatureComponents.forEach((component, index) => {
                component.sequentialId = index + 1
            })
        },
        calculatePageRange(component) {
            if (!component.sequentialId) {
                this.assignSequentialIds()
                
            }
            if (!component.numberOfCopies || parseInt(component.numberOfCopies) === 0) {
                return 'Składki: -'
            }

            const signatureComponents = this.productTemplate.jsonTemplate.orderProductComponents
                .filter(comp => comp.component.code === 'skladka')
            const sortedComponents = [...signatureComponents].sort((a, b) => a.sequentialId - b.sequentialId)
            
            // starting page number
            let startPage = 1
            
            for (let i = 0; i < component.sequentialId - 1; i++) {
                const comp = sortedComponents[i]
                
                if (comp && comp.numberOfCopies && parseInt(comp.numberOfCopies) > 0) {
                    startPage += parseInt(comp.numberOfCopies)
                }
            }
            
            // ending page number
            const currentQuantity = parseInt(component.numberOfCopies) || 1
            const endPage = startPage + currentQuantity - 1
            
            return `Składki: ${startPage} - ${endPage}`
        },
        fillComponentsFromProductType(productTypeId) {
            const productType = this.products.find(product => product.id === productTypeId)

            if (!productType) {
                return
            }
            
            this.productTemplate.jsonTemplate.productTypeId = productTypeId
            this.productTemplate.jsonTemplate.orderProductComponents = []
            
            // Process each component from the product type
            if (productType.productTypeComponent && Array.isArray(productType.productTypeComponent)) {
                productType.productTypeComponent.forEach(typeComponent => {
                    const component = {
                        component: typeComponent.component,
                        componentId: typeComponent.componentId,
                        orderComponentProcesses: [],
                        orderComponentProperties: []
                    }

                    if (productType.productTypeComponent[0]?.component?.code === 'skladka') {
                        component.numberOfCopies = 0
                    }
                
                    // Add production processes
                    if (typeComponent.productTypeComponentProcesses && Array.isArray(typeComponent.productTypeComponentProcesses)) {
                        typeComponent.productTypeComponentProcesses.forEach((process, index) => {
                            component.orderComponentProcesses.push({
                                order: process.order || index + 1,
                                productionProcessId: process.productionProcessId,
                                productionProcess: process.productionProcess
                            })
                        })
                    }
                
                    // Add component properties
                    if (typeComponent.productTypeComponentProperties && Array.isArray(typeComponent.productTypeComponentProperties)) {
                        typeComponent.productTypeComponentProperties.forEach(property => {
                            component.orderComponentProperties.push({
                                productProductionPropertyId: property.productProductionPropertyId,
                                productProductionProperty: property.productProductionProperty,
                                value: property.productProductionProperty.defaultValue || ''
                            })
                        })
                    }
                
                    this.productTemplate.jsonTemplate.orderProductComponents.push(component)
                })
            }
            
            this.hasProductChanges = true
        },
        onRadioChange(value) {
            this.currentProductionFile = null
            this.fileLink = null
            
            if (value === 'upload') {
                this.removeAttachments('link')
            } else if (value === 'link') {
                this.removeAttachments('file')
            }
        },
        handleProductTypeChange() {
            if (!this.userEditedComponents) {
                this.clearAllAttachments()
                this.fillComponentsFromProductType(this.productTemplate.jsonTemplate.productTypeId)
                this.assignSequentialIds()
            }
        },
        clearAllAttachments() {
            if (this.product.productionFiles) {
                ['file', 'link'].forEach(type => this.removeAttachments(type))
            }
            this.currentProductionFile = null
            if (this.$v.currentProductionFile) {
                this.$v.currentProductionFile.$reset()
            }
        },
        removeAttachments(type) {
            const attachmentsToDelete = type !== 'file' ?
                this.product.productionFiles.filter(item => item.urlToExternalFile) :
                this.product.productionFiles.filter(item => item.file)
            
            attachmentsToDelete.forEach(attachment => {
                this.deleteProductionFileAttachment(attachment.id)
            })
        },
        canUploadFile() {
            if (this.proofProductType) {
                if (this.product.productionFiles.length > 0) {
                    return false
                }

                if (this.currentProductionFile && 
                    this.currentProductionFile.size > appConfig.orders.productionFileAttachmentSizeProof * 1024 * 1024) {
                    return false
                }
            }

            return this.currentProductionFile && !this.$v.currentProductionFile.$invalid
        },
        addProductionFileAttachment() {
            if (!this.currentProductionFile || !this.canUploadFile()) {
                this.$v.currentProductionFile.$touch()
                
                return
            }

            const formData = new FormData()

            formData.append('file', this.currentProductionFile)

            this.actionLoading.fileUploading = true

            this.$store.dispatch('orders/addProductionFileAttachment', {
                formData: formData,
                abort: false
            })
                .then(response => {
                    this.product.productionFiles.push(response.data)
                    this.currentProductionFile = null
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.fileUploading = false
                })
        },
        addProductionFileAttachmentLink() {
            this.actionLoading.fileUploading = true

            this.$store.dispatch('orders/addProductionFileAttachmentLink', {
                urlToExternalFile: this.fileLink,
            })
                .then(response => {
                    this.product.productionFiles.push(response.data)
                    this.fileLink = null
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.fileUploading = false
                })
        },
        abortUploadingProductionFileAttachment() {
            this.$store.dispatch('orders/addProductionFileAttachment', {
                formData: null,
                abort: true
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        deleteProductionFileAttachment(id) {
            this.actionLoading.fileRemoving = true
            this.$store.dispatch('orders/deleteProductionFileAttachment', id)
                .then(() => {
                    const index = this.product.productionFiles.findIndex(item => item.id == id)

                    this.product.productionFiles.splice(index, 1)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.fileRemoving = false
                })
        },
        showAssignProductTemplateDialog() {
            store.dispatch('orders/getProductTemplates')
                .then(() => {
                    this.assignProductTemplateDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeAssignProductTemplateDialog() {
            this.assignProductTemplateDialog = false
        },
        showAssignComponentDialog() {
            this.assignComponentDialog = true
        },
        closeAssignComponentDialog() {
            this.assignComponentDialog = false
        },
        addProductComponent(component) {
            this.userEditedComponents = true
            this.hasProductChanges = true
            let newComponent = {
                component: component,
                componentId: component.id,
                orderComponentProcesses: [],
                orderComponentProperties: [],
            }

            if (component.code === 'skladka') {
                newComponent['numberOfCopies'] = 0
            }

            this.productTemplate.jsonTemplate.orderProductComponents.push(newComponent)

            if (component.code === 'skladka') {
                this.assignSequentialIds()
                this.calculatePageRange(newComponent)
            }
        },
        removeProductComponent(index) {
            this.hasProductChanges = true

            const isRemovingSignatureComponent = this.productTemplate.jsonTemplate.orderProductComponents[index]?.component?.code === 'skladka'

            this.productTemplate.jsonTemplate.orderProductComponents.splice(index, 1)

            if (isRemovingSignatureComponent) {
                this.assignSequentialIds()
            }
            // allow to fill default components if user removed all components
            if (index === 0 && this.productTemplate.jsonTemplate.orderProductComponents.length === 0 ) {
                this.userEditedComponents = false
            }
            this.closeConfirmRemoveComponentDialog()
        },
        showConfirmRemoveComponentDialog(componentIndex) {
            this.confirmRemoveComponentDialog = true
            this.currentProductionComponentIndex = componentIndex
        },
        closeConfirmRemoveComponentDialog() {
            this.confirmRemoveComponentDialog = false
            this.clearCurrentProductionComponent()
        },
        showAssignProductionPropertiesDialog(component, index) {
            this.assignProductionPropertiesDialog = true
            this.currentProductionComponent = component
            this.currentProductionComponentIndex = index
        },
        closeAssignProductionPropertiesDialog() {
            this.assignProductionPropertiesDialog = false
            setTimeout(() => {
                this.clearCurrentProductionComponent()
            }, 500)
        },
        saveProductProductionProperties(productionProperties) {
            productionProperties.forEach(item => {
                this.productTemplate.jsonTemplate.orderProductComponents[this.currentProductionComponentIndex].orderComponentProperties.push({
                    productProductionPropertyId: item.id,
                    productProductionProperty: item
                })
            })

            this.hasProductChanges = true
        },
        removeProductProductionProperty(componentIndex, productionProperty) {
            const orderComponentProperties = this.productTemplate.jsonTemplate.orderProductComponents[componentIndex].orderComponentProperties

            orderComponentProperties.splice(orderComponentProperties.indexOf(productionProperty), 1)

            this.hasProductChanges = true
        },
        showAssignProductionProcessDialog(component, index) {
            this.assignProductionProcessDialog = true
            this.currentProductionComponent = component
            this.currentProductionComponentIndex = index
        },
        saveProductionProcess(productionProcesses) {
            productionProcesses.forEach(item => {

                this.productTemplate.jsonTemplate.orderProductComponents[this.currentProductionComponentIndex].orderComponentProcesses.push({
                    productionProcessId: item.id,
                    productionProcess: item
                })
            })

            this.hasProductChanges = true
        },
        removeProductionProcess(componentIndex, productionProcess) {
            const orderComponentProcesses = this.productTemplate.jsonTemplate.orderProductComponents[componentIndex].orderComponentProcesses

            orderComponentProcesses.splice(orderComponentProcesses.indexOf(productionProcess), 1)

            this.hasProductChanges = true
        },
        closeProductionProcessDialog() {
            this.assignProductionProcessDialog = false

            setTimeout(() => {
                this.clearCurrentProductionComponent()
            }, 500)
        },
        addProductTemplate(payload) {
            this.assignProductTemplateDialog = false
            store.dispatch('orders/getProductTemplate', payload.productTemplateId)
                .then(response => {
                    this.productTemplate = Object.assign({}, response.data)
                    this.assignSequentialIds()
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        saveProduct() {
            const saveProduct = () => {
                const actionName = this.actionName === 'addProduct' ? 'updateOrderCreateProductList' : 'updateOrderCreateProductTemplate'

                this.userEditedComponents = false
                this.hasProductChanges = false
                this.$store.dispatch(`orders/${actionName}`, {
                    index: this.productTemplateIndex,
                    product: {
                        name: this.productTemplate.name,
                        productTypeId: this.productTemplate.jsonTemplate.productTypeId,
                        amount: this.product.amount,
                        description: this.productTemplate.jsonTemplate.description,
                        numberOfVersions: this.product.numberOfVersions,
                        orderProductComponents: this.productTemplate.jsonTemplate.orderProductComponents,
                        weight: this.product.weight,
                        destinyPaperId: this.product.destinyPaperId,
                        productionFiles: this.product.productionFiles,
                        orderProductAttachmentId: this.product.productionFiles.map(item => item.id),
                        formatA: this.productTemplate.jsonTemplate.formatA,
                        formatB: this.productTemplate.jsonTemplate.formatB
                    },
                    productTemplate: this.productTemplate
                })
                this.$router.push({
                    name: 'ordersInternalOrdersOrderCreate'
                })
                    .catch(error => {
                        errorHandler(error)
                    })
            }

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else if (this.productTemplate.jsonTemplate.orderProductComponents.length === 0) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.noComponents')
                })
            } else {
                const payload = {
                    name: this.productTemplate.name,
                    jsonTemplate: JSON.stringify(this.productTemplate.jsonTemplate)
                }

                if (this.saveProductTemplate) {
                    this.$store.dispatch('orders/createProductTemplate', payload)
                        .then(() => {
                            saveProduct()
                        })
                        .catch(error => {
                            errorHandler(error)
                        })
                } else {
                    saveProduct()
                }
            }
        },
        closeConfirmLeaveDialog() {
            this.confirmLeaveDialog = false
        },
        acceptConfirmLeaveDialog() {
            this.hasProductChanges = false
            this.$router.push({ name: this.confirmLeaveDialogNextRouteName })
                .catch(error => {
                    errorHandler(error)
                })
        },
        clearCurrentProductionComponent() {
            this.currentProductionComponent = {
                component: {
                    name: null,
                    code: null
                },
                orderComponentProperties: [],
                orderComponentProcesses: []
            }
            this.currentProductionComponentIndex = null
        },
        productionFileName(item) {
            if (!item.file && item.urlToExternalFile) {
                return item.urlToExternalFile
            }

            const words = item.file.split('/')

            return words[words.length-1]
        },
        close() {
            this.$router.push({ name: 'ordersInternalOrdersOrderCreate' })
                .catch(error => {
                    errorHandler(error)
                })
        },

        // Walidacje
        productTemplateNameErrors() {
            const errors = []

            if (!this.$v.productTemplate.name.$dirty) return errors
            !this.$v.productTemplate.name.required && errors.push('Proszę podać nazwę produktu.')

            return errors
        },
        productTemplateProductTypeErrors() {
            const errors = []

            if (!this.$v.productTemplate.jsonTemplate.productTypeId.$dirty) return errors
            !this.$v.productTemplate.jsonTemplate.productTypeId.required && errors.push('Proszę podać rodzaj produktu.')

            return errors
        },
        productTemplateFormatAErrors() {
            if (this.productTemplate.jsonTemplate.formatA) {
                const errors = []

                if (!this.$v.productTemplate.jsonTemplate.formatA.$dirty) return errors
                this.$v.productTemplate.jsonTemplate.formatA.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productTemplateFormatBErrors() {
            if (this.productTemplate.jsonTemplate.formatB) {
                const errors = []

                if (!this.$v.productTemplate.jsonTemplate.formatB.$dirty) return errors
                this.$v.productTemplate.jsonTemplate.formatB.$invalid && errors.push('Nieprawidłowa wartość.')

                return errors
            }
        },
        productAmountErrors() {
            const errors = []

            if (!this.$v.product.amount.$dirty) return errors
            !this.$v.product.amount.required && errors.push('Proszę podać nakład.')

            return errors
        },
        productNumberOfVersionsErrors() {
            const errors = []

            if (!this.$v.product.numberOfVersions.$dirty) return errors
            !this.$v.product.numberOfVersions.required && errors.push('Proszę podać ilość wzorów.')
            !this.$v.product.numberOfVersions.minValue && errors.push('Ilość wzorów musi być większa od 0.')

            return errors
        },
        currentProductionFileErrors() {
            if (!this.currentProductionFile) return

            const errors = []

            if (!this.$v.currentProductionFile.$dirty) return errors
            this.currentProductionFileRules.map(e => errors.push(e))

            return errors
        },
        productionPropertyValueErrors(componentIndex, propertyIndex) {
            const errors = []

            if (!this.$v.productTemplate.jsonTemplate.orderProductComponents.$each[componentIndex].orderComponentProperties.$each[propertyIndex].$dirty) return errors

            !this.$v.productTemplate.jsonTemplate.orderProductComponents.$each[componentIndex].orderComponentProperties.$each[propertyIndex].value.required && errors.push('Proszę podać wartość.')

            return errors
        },
        productDestinyPaperErrors() {
            const errors = []

            if (!this.$v.product.destinyPaperId.$dirty) return errors
            !this.$v.product.destinyPaperId.required && errors.push('Proszę podać rodzaj papieru docelowej produkcji.')

            return errors
        },
        numberOfCopiesErrors(component) {
            const errors = []
            
            if (component.component.code === 'skladka') {

                const componentIndex = this.productTemplate.jsonTemplate.orderProductComponents.findIndex(
                    comp => comp === component
                )
                
                if (this.$v.productTemplate.jsonTemplate.orderProductComponents.$each[componentIndex]) {
                    const validation = this.$v.productTemplate.jsonTemplate.orderProductComponents.$each[componentIndex].numberOfCopies
                    
                    if (validation && validation.$dirty) {
                        !validation.required && errors.push('Pole wymagane')
                        !validation.minValue && errors.push('Wartość musi być liczbą dodatnią')
                    }
                }
            }
            
            return errors
        },
    }
}
</script>
